import React, { useEffect, useState, useRef } from 'react';
import API_URL from '../config';
import Header from './Header';
import PedidosPage from './PedidosPage';

const tiposTabs = [
  { label: 'Mesas', value: 'M', cor: 'green' },
  { label: 'Comandas', value: 'C', cor: 'blue' },
  { label: 'Fichas', value: 'F', cor: 'yellow' },
  { label: 'Serviços', value: 'S', cor: 'pink' },
];

const statusMap = {
  0: { label: 'LIVRE', cor: 'border-green-500 text-green-600', bg: 'bg-white' },
  1: { label: 'ABERTO', cor: 'border-blue-500 text-blue-600', bg: 'bg-blue-50' },
  2: { label: 'EM FECHAMENTO', cor: 'border-yellow-500 text-yellow-600', bg: 'bg-yellow-50' },
  3: { label: 'RESERVADO', cor: 'border-purple-500 text-purple-600', bg: 'bg-purple-50' },
  4: { label: 'BLOQUEADO', cor: 'border-red-500 text-red-600', bg: 'bg-red-50' },
  5: { label: 'LIMPEZA', cor: 'border-gray-400 text-gray-600', bg: 'bg-gray-100' },
  6: { label: 'EM USO', cor: 'border-orange-500 text-orange-600', bg: 'bg-orange-50' },
};

function AtendimentosPage({ empresa, usuario, onVoltar, onSair }) {
  const [tab, setTab] = useState('M');
  const [busca, setBusca] = useState('');
  const [dados, setDados] = useState([]);
  const [tiposDisponiveis, setTiposDisponiveis] = useState({});
  const [primeiroCarregamento, setPrimeiroCarregamento] = useState(true);
  const [agora, setAgora] = useState(Date.now());
  const intervalRef = useRef();
  const [modalPedidoId, setModalPedidoId] = useState(null);

  useEffect(() => {
    // Buscar todos os tipos para saber quais abas mostrar
    const buscarTipos = async () => {
      const tipos = {};
      for (const t of tiposTabs) {
        const resp = await fetch(`${API_URL}/atendimento_config?empresa_id=${empresa.id}&tipo=${t.value}`);
        const data = await resp.json();
        if (Array.isArray(data) && data.length > 0) tipos[t.value] = true;
      }
      setTiposDisponiveis(tipos);
      // Se a aba atual não tem dados, muda para a primeira disponível
      if (!tipos[tab]) {
        const first = tiposTabs.find(t => tipos[t.value]);
        if (first) setTab(first.value);
      }
    };
    buscarTipos();
    buscarDados(true);

    // Polling para atualização automática
    const interval = setInterval(() => {
      buscarDados(false);
    }, 5000); // 5 segundos

    // Intervalo para atualizar o tempo decorrido em cards ABERTO
    intervalRef.current = setInterval(() => {
      setAgora(Date.now());
    }, 1000);

    return () => {
      clearInterval(interval);
      clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line
  }, [tab, empresa.id]);

  const buscarDados = async (mostrarLoading = false) => {
    const resp = await fetch(`${API_URL}/atendimento_config?empresa_id=${empresa.id}&tipo=${tab}`);
    const data = await resp.json();
    // Só atualiza se mudou
    if (JSON.stringify(Array.isArray(data) ? data : []) !== JSON.stringify(dados)) {
      setDados(Array.isArray(data) ? data : []);
    }
    if (primeiroCarregamento) setPrimeiroCarregamento(false);
  };

  const dadosFiltrados = dados.filter(d => d.nome.toLowerCase().includes(busca.toLowerCase()));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-blue-300">
      <Header usuario={usuario} empresa={empresa} onVoltar={onVoltar} onSair={onSair} />
      <div className="flex flex-col items-center justify-center mt-16">
        <div className="bg-white p-10 rounded-2xl shadow-2xl w-full max-w-3xl border border-blue-100 flex flex-col items-center">
          <h2 className="text-2xl font-bold text-blue-700 mb-8">Atendimentos</h2>
          {/* Tabs */}
          <div className="flex border-b mb-4 w-full">
            {tiposTabs.filter(t => tiposDisponiveis[t.value]).map(t => (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className={`px-4 py-2 font-semibold border-b-2 transition-colors ${tab === t.value ? 'border-blue-700 text-blue-700' : 'border-transparent text-gray-500'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* Busca */}
          <div className="flex items-center gap-2 mb-4 w-full">
            <input
              type="text"
              placeholder="Buscar por nome"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              className="border rounded px-3 py-2 flex-1"
            />
            <button className="p-2 text-blue-700 border border-blue-200 rounded hover:bg-blue-50">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
            </button>
          </div>
          {/* Grid de cards */}
          {primeiroCarregamento ? (
            <div className="text-center py-8 text-gray-500">Carregando...</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 w-full">
              {dadosFiltrados.map((d, i) => {
                const statusInfo = statusMap[d.status] || statusMap[0];
                // Definir cor de fundo do topo do card
                const topBgMap = {
                  0: 'bg-green-500',
                  1: 'bg-blue-500',
                  2: 'bg-orange-400',
                  3: 'bg-purple-500',
                  4: 'bg-red-500',
                  5: 'bg-yellow-900', // marrom
                  6: 'bg-gray-400',
                };
                const topBg = topBgMap[d.status] || 'bg-green-500';
                const topTextMap = {
                  0: 'text-white',
                  1: 'text-white',
                  2: 'text-white',
                  3: 'text-white',
                  4: 'text-white',
                  5: 'text-white',
                  6: 'text-white',
                };
                const topText = topTextMap[d.status] || 'text-white';
                // Função para calcular tempo decorrido
                function tempoDecorrido(abertura) {
                  if (!abertura) return '00:00:00';
                  const inicio = new Date(abertura).getTime();
                  const diff = Math.max(0, agora - inicio);
                  const h = Math.floor(diff / 3600000).toString().padStart(2, '0');
                  const m = Math.floor((diff % 3600000) / 60000).toString().padStart(2, '0');
                  const s = Math.floor((diff % 60000) / 1000).toString().padStart(2, '0');
                  return `${h}:${m}:${s}`;
                }
                return (
                  <div
                    key={d.id || i}
                    className={`rounded-xl border-2 ${statusInfo.cor} bg-white flex flex-col items-center justify-start shadow-md min-h-[140px] cursor-pointer`}
                    onClick={() => { if (d.pedido_id) setModalPedidoId(d.pedido_id); }}
                  >
                    <div className={`w-full rounded-t-xl ${topBg} ${topText} text-xs font-bold flex items-center justify-center h-7 tracking-widest`}>{d.nome?.toUpperCase()}</div>
                    <div className={`flex-1 flex flex-col items-center justify-center py-4`}> 
                      <div className={`text-base font-semibold ${statusInfo.cor}`}>{statusInfo.label}</div>
                      {d.status === 1 && (
                        <div className="text-sm text-gray-800 mt-1 font-mono">{tempoDecorrido(d.abertura)}</div>
                      )}
                    </div>
                  </div>
                );
              })}
              {dadosFiltrados.length === 0 && (
                <div className="col-span-full text-center py-8 text-gray-400">Nenhum registro encontrado.</div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Modal PedidosPage */}
      {modalPedidoId && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-0 w-full max-w-lg relative">
            <button onClick={() => setModalPedidoId(null)} className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-2xl z-10">&times;</button>
            <PedidosPage
              usuario={usuario}
              empresa={empresa}
              pedidoId={modalPedidoId}
              onVoltar={() => setModalPedidoId(null)}
              onSair={onSair}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AtendimentosPage; 